import React from "react";
import RouterLink from "next/link";
import { useQuery } from "@apollo/client/react";

import { CircleHelp, LogOut, Mail, Plus, Settings, User } from "lucide-react";

import { cn, textVariants, Link, LinkVariant } from "@jasperlabs/ui";

import { queries } from "@/api";
import { useLogOut } from "@/contexts/logOut";
import ContactUs from "@/tenants/components/TenantSupportLink";
import { CARUSO_HELP_CENTER } from "@/constants/external-links";
import { formatFullName } from "@/components/utilities/stringFormat";

const ITEM_STYLES = "px-6 py-4";
const LINK_STYLES = cn(
	textVariants({ variant: "subheading-default" }),
	"font-semibold flex w-full items-center text-black",
);

type AccountNavViewProps = {
	onClose: () => void;
};

/**
 * "My Account" navigation shown when the account toggle is active. Mirrors the
 * desktop account dropdown: account/entity links plus an identity card.
 */
const AccountNavView = ({ onClose }: AccountNavViewProps) => {
	const { logOut } = useLogOut();

	const { data = {} } = useQuery(queries.ME);
	const { account = null } = data;
	const { firstName, lastName, email } = account || {};
	const fullName = formatFullName({ firstName, lastName });
	const avatarInitial = (firstName || fullName || "")
		.trim()
		.charAt(0)
		.toUpperCase();

	return (
		<div className="flex w-full flex-col">
			<ul className="flex w-full flex-col divide-y divide-neutral-200">
				<li className={ITEM_STYLES}>
					<Link
						as={RouterLink}
						icon={<User className="size-[18px]" />}
						variant={LinkVariant.Black}
						className={LINK_STYLES}
						href="/preferences"
						onClick={onClose}
					>
						Account preferences
					</Link>
				</li>
				<li className={ITEM_STYLES}>
					<Link
						as={RouterLink}
						icon={<Settings className="size-[18px]" />}
						variant={LinkVariant.Black}
						className={LINK_STYLES}
						href="/investing-entities/details"
						onClick={onClose}
					>
						Investing entity settings
					</Link>
				</li>
				<li className={ITEM_STYLES}>
					<Link
						as={RouterLink}
						icon={<Plus className="size-[18px]" />}
						variant={LinkVariant.Black}
						className={LINK_STYLES}
						href="/add-investing-entity"
						onClick={onClose}
					>
						Add investing entity
					</Link>
				</li>
				<li className={ITEM_STYLES}>
					<ContactUs
						icon={<Mail className="size-[18px]" />}
						className={LINK_STYLES}
					>
						Contact us
					</ContactUs>
				</li>
				<li className={ITEM_STYLES}>
					<Link
						icon={<CircleHelp className="size-[20px]" />}
						variant={LinkVariant.Black}
						className={LINK_STYLES}
						href={CARUSO_HELP_CENTER}
						target="_blank"
						onClick={onClose}
					>
						Help
					</Link>
				</li>
				<li className={ITEM_STYLES}>
					<Link
						icon={<LogOut className="size-[18px]" />}
						variant={LinkVariant.Black}
						className={LINK_STYLES}
						as="button"
						onClick={logOut}
					>
						Log out
					</Link>
				</li>
			</ul>
			{account && (
				<div className="flex items-center gap-3 border-t border-neutral-200 px-6 py-4">
					<div
						aria-hidden="true"
						className={cn(
							textVariants({ variant: "subheading-sm" }),
							"flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-700",
						)}
					>
						{avatarInitial}
					</div>
					<div className="min-w-0">
						<div
							className={cn(
								textVariants({ variant: "subheading-sm" }),
								"truncate text-neutral-800",
							)}
							aria-label={fullName}
						>
							{fullName}
						</div>
						<div
							className={cn(
								textVariants({ variant: "body-sm" }),
								"truncate text-neutral-500",
							)}
						>
							User ID: {account?.id}
						</div>
						<div
							className={cn(
								textVariants({ variant: "body-sm" }),
								"truncate text-neutral-500",
							)}
							aria-label={email}
						>
							{email}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default AccountNavView;
